/**
 * Procurement API Client
 * Frontend API calls for weekly procurement planning.
 */

import { apiCall } from '../api';
import type {
  AddManualProcurementItemRequest,
  BulkInventoryEntry,
  BulkInventoryResponse,
  GenerateProcurementRequest,
  ProcurementItem,
  ProcurementItemResponse,
  ProcurementListResponse,
  UpdateProcurementItemRequest,
} from '@/types/api';
import type { ProcurementExportGroup } from '@/lib/utils/procurementExportWorkbook';

export async function fetchProcurement(weekStart: string): Promise<ProcurementListResponse> {
  const params = new URLSearchParams({ weekStart });
  return apiCall<ProcurementListResponse>(`/api/procurement?${params.toString()}`, {
    method: 'GET',
  });
}

export async function generateProcurement(
  data: GenerateProcurementRequest
): Promise<ProcurementListResponse> {
  return apiCall<ProcurementListResponse>('/api/procurement/generate', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateProcurementItem(
  itemId: number,
  data: UpdateProcurementItemRequest
): Promise<ProcurementItem> {
  const response = await apiCall<ProcurementItemResponse>(`/api/procurement/items/${itemId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  return response.procurementItem;
}

export async function addManualProcurementItem(
  procurementListId: number,
  data: AddManualProcurementItemRequest
): Promise<ProcurementItem> {
  const response = await apiCall<ProcurementItemResponse>(`/api/procurement/${procurementListId}/items`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.procurementItem;
}

export async function deleteProcurementItem(itemId: number): Promise<void> {
  await apiCall<{ message: string }>(`/api/procurement/items/${itemId}`, {
    method: 'DELETE',
  });
}

export async function saveInventory(
  listId: number,
  entries: BulkInventoryEntry[],
): Promise<BulkInventoryResponse> {
  return apiCall<BulkInventoryResponse>(`/api/procurement/${listId}/inventory`, {
    method: 'PUT',
    body: JSON.stringify({ entries }),
  });
}

export function getFilenameFromDisposition(disposition: string | null, fallback: string) {
  if (!disposition) {
    return fallback;
  }

  const utfMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utfMatch?.[1]) {
    return decodeURIComponent(utfMatch[1]);
  }

  const filenameMatch = disposition.match(/filename="?([^"]+)"?/i);
  return filenameMatch?.[1] ?? fallback;
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export async function exportProcurementXLSX(procurementListId: number): Promise<void> {
  const response = await fetch(`/api/procurement/${procurementListId}/export`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({
      error: `HTTP ${response.status}`,
    }));
    throw new Error(error.error || `Export failed with status ${response.status}`);
  }

  const blob = await response.blob();
  const filename = getFilenameFromDisposition(
    response.headers.get('Content-Disposition'),
    `procurement-list-${procurementListId}.xlsx`,
  );

  downloadBlob(blob, filename);
}

export async function exportProcurementDraftXLSX(
  groups: ProcurementExportGroup[],
  filename: string,
  warnings: string[] = [],
): Promise<void> {
  const response = await fetch('/api/procurement/export-draft', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ groups, filename, warnings }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({
      error: `HTTP ${response.status}`,
    }));
    throw new Error(error.error || `Export failed with status ${response.status}`);
  }

  const blob = await response.blob();
  const serverFilename = getFilenameFromDisposition(
    response.headers.get('Content-Disposition'),
    filename,
  );

  downloadBlob(blob, serverFilename);
}
